import React, { useState, useEffect } from 'react';
import { mealsAPI } from '../../services/meals.api';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Alert,
  Snackbar,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  Avatar
} from '@mui/material';

// Using standard MUI components instead of date pickers

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Restaurant,
  CalendarMonth,
  ArrowBackIos,
  ArrowForwardIos,
  CheckCircle,
  RadioButtonUnchecked,
  LocalDining,
  FreeBreakfast,
  BrunchDining,
  LunchDining,
  DinnerDining,
  Coffee
} from '@mui/icons-material';

// Simplified date utilities without date-fns dependency
const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDay = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const isToday = (date) => {
  const today = new Date();
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
};

const isSameDay = (date1, date2) => {
  date1 = new Date(date1);
  date2 = new Date(date2);
  return date1.getDate() === date2.getDate() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getFullYear() === date2.getFullYear();
};

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // Sunday - Saturday : 0 - 6
  d.setDate(d.getDate() - day);
  return d;
};

const endOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // Sunday - Saturday : 0 - 6
  d.setDate(d.getDate() + (6 - day));
  return d;
};

const eachDayOfInterval = ({ start, end }) => {
  const days = [];
  let current = new Date(start);
  
  while (current <= end) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return days;
};

const formatDateToYYYYMMDD = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to get meal type icon
const getMealTypeIcon = (type) => {
  switch(type?.toLowerCase()) {
    case 'breakfast': return <FreeBreakfast />;
    case 'brunch': return <BrunchDining />;
    case 'lunch': return <LunchDining />;
    case 'hi-tea': return <Coffee />;
    case 'dinner': return <DinnerDining />;
    default: return <Restaurant />;
  }
};

const MealPlanner = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  
  // All available meals to choose from
  const [availableMeals, setAvailableMeals] = useState([]);
  const [selectedMeal, setSelectedMeal] = useState(null);
  
  // Dialog states
  const [open, setOpen] = useState(false);
  const [planDate, setPlanDate] = useState(new Date());
  const [mealType, setMealType] = useState('');
  const [notes, setNotes] = useState('');
  const [mealSearchQuery, setMealSearchQuery] = useState('');
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [viewMode, setViewMode] = useState('week');

  // Get the dates for the current week
  useEffect(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 }); // 0 means Sunday
    const end = endOfWeek(currentDate, { weekStartsOn: 0 });
    const dates = eachDayOfInterval({ start, end });
    setWeekDates(dates);
    
    // Fetch meal plans for the current week
    fetchMealPlans(start, end);
  }, [currentDate]);

  // Fetch all available meals
  useEffect(() => {
    fetchAvailableMeals();
  }, []);

  const fetchMealPlans = async (start, end) => {
    try {
      setLoading(true);
      const response = await mealsAPI.getMealPlans({ startDate: start, endDate: end });
      setMealPlans(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
      setError('Failed to load meal plans');
      setLoading(false);
    }
  };

  const fetchAvailableMeals = async () => {
    try {
      const response = await mealsAPI.getAllMeals();
      setAvailableMeals(response.data);
    } catch (error) {
      console.error('Error fetching available meals:', error);
      setNotification({
        open: true,
        message: 'Failed to load meals. Some features may be limited.',
        severity: 'warning'
      });
    }
  };

  const handlePrevWeek = () => {
    setCurrentDate(addDays(currentDate, -7));
  };

  const handleNextWeek = () => {
    setCurrentDate(addDays(currentDate, 7));
  };

  const handleOpen = (date, type = '') => {
    setPlanDate(date);
    setMealType(type);
    setSelectedMeal(null);
    setNotes('');
    setEditingPlanId(null);
    setOpen(true);
  };

  const handleEdit = (plan) => {
    setPlanDate(new Date(plan.plannedDate));
    setMealType(plan.mealType);
    setSelectedMeal(plan.meal);
    setNotes(plan.notes || '');
    setEditingPlanId(plan._id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    if (!selectedMeal || !mealType) {
      setNotification({
        open: true,
        message: 'Please select a meal and meal type',
        severity: 'error'
      });
      return;
    }

    try {
      const mealPlanData = {
        mealId: selectedMeal._id,
        plannedDate: planDate,
        mealType,
        notes
      };

      if (editingPlanId) {
        // Update existing meal plan
        await mealsAPI.updateMealPlan(editingPlanId, mealPlanData);
        setNotification({
          open: true,
          message: 'Meal plan updated successfully',
          severity: 'success'
        });
      } else {
        // Add new meal plan
        await mealsAPI.addMealPlan(mealPlanData);
        setNotification({
          open: true,
          message: 'Meal added to your plan',
          severity: 'success'
        });
      }

      // Refresh meal plans
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      fetchMealPlans(start, end);

      // Close dialog
      handleClose();
    } catch (error) {
      console.error('Error saving meal plan:', error);
      setNotification({
        open: true,
        message: 'Failed to save meal plan',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await mealsAPI.deleteMealPlan(id);
      
      // Update UI
      setMealPlans(mealPlans.filter(plan => plan._id !== id));
      
      setNotification({
        open: true,
        message: 'Meal plan deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting meal plan:', error);
      setNotification({
        open: true,
        message: 'Failed to delete meal plan',
        severity: 'error'
      });
    }
  };

  const handleToggleComplete = async (plan) => {
    try {
      const updatedPlan = await mealsAPI.updateMealPlan(plan._id, {
        completed: !plan.completed
      });
      
      // Update UI
      setMealPlans(mealPlans.map(p => p._id === plan._id ? updatedPlan.data : p));
      
      setNotification({
        open: true,
        message: `Meal ${updatedPlan.data.completed ? 'marked as cooked' : 'unmarked'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating meal plan:', error);
      setNotification({
        open: true,
        message: 'Failed to update meal plan',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  const getMealsByDateAndType = (date, type) => {
    return mealPlans.filter(plan => 
      isSameDay(new Date(plan.plannedDate), date) && 
      plan.mealType === type
    );
  };

  // Filter available meals based on search query
  const filteredMeals = mealSearchQuery.trim() === '' 
    ? availableMeals 
    : availableMeals.filter(meal => 
        meal.name.toLowerCase().includes(mealSearchQuery.toLowerCase()) ||
        (meal.description && meal.description.toLowerCase().includes(mealSearchQuery.toLowerCase()))
      );

  // List of meal types
  const mealTypes = ['breakfast', 'brunch', 'lunch', 'hi-tea', 'dinner'];

  return (
    <div>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            <CalendarMonth sx={{ mr: 1, verticalAlign: 'middle' }} />
            Meal Planner
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen(new Date())}
            >
              Add Meal to Plan
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <IconButton onClick={handlePrevWeek}>
              <ArrowBackIos />
            </IconButton>
            
            <Typography variant="h6">
              Week of {formatDate(weekDates[0])}
            </Typography>
            
            <IconButton onClick={handleNextWeek}>
              <ArrowForwardIos />
            </IconButton>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {weekDates.map(date => (
                <Grid item xs={12} key={date.toString()}>
                  <Paper 
                    elevation={isToday(date) ? 3 : 1} 
                    sx={{ 
                      p: 2, 
                      bgcolor: isToday(date) ? 'primary.light' : 'background.paper',
                      color: isToday(date) ? 'primary.contrastText' : 'text.primary' 
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {formatDay(date)}
                      {isToday(date) && (
                        <Chip 
                          label="Today" 
                          color="primary" 
                          size="small" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {mealTypes.map(type => {
                        const mealsForType = getMealsByDateAndType(date, type);
                        return (
                          <Grid item xs={12} md={mealsForType.length > 0 ? 6 : 'auto'} key={type}>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                mb: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                pb: 0.5
                              }}
                            >
                              {getMealTypeIcon(type)}
                              <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', ml: 1 }}>
                                {type}
                              </Typography>
                              <IconButton 
                                size="small" 
                                sx={{ ml: 'auto' }}
                                onClick={() => handleOpen(date, type)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            {mealsForType.length > 0 ? (
                              mealsForType.map(plan => (
                                <Card
                                  key={plan._id}
                                  sx={{ 
                                    mb: 2, 
                                    display: 'flex',
                                    opacity: plan.completed ? 0.7 : 1
                                  }}
                                >
                                  <CardMedia
                                    component="img"
                                    sx={{ width: 90, height: 90 }}
                                    image={plan.meal.imageUrl || `https://source.unsplash.com/random/90x90/?food`}
                                    alt={plan.meal.name}
                                  />
                                  <CardContent sx={{ flex: '1 0 auto', pb: '8px !important' }}>
                                    <Box display="flex" justifyContent="space-between">
                                      <Typography variant="subtitle1" noWrap sx={{ maxWidth: '70%' }}>
                                        {plan.meal.name}
                                      </Typography>
                                      <Box>
                                        <IconButton 
                                          size="small" 
                                          onClick={() => handleToggleComplete(plan)}
                                          color={plan.completed ? "primary" : "default"}
                                        >
                                          {plan.completed ? 
                                            <CheckCircle fontSize="small" /> : 
                                            <RadioButtonUnchecked fontSize="small" />}
                                        </IconButton>
                                        <IconButton 
                                          size="small"
                                          onClick={() => handleEdit(plan)}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                          size="small" 
                                          color="error"
                                          onClick={() => handleDelete(plan._id)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                    {plan.notes && (
                                      <Typography variant="body2" color="text.secondary">
                                        {plan.notes}
                                      </Typography>
                                    )}
                                    <Box display="flex" mt={1}>
                                      {plan.meal.dietaryType?.map((type, index) => (
                                        <Typography 
                                          key={index} 
                                          variant="caption" 
                                          sx={{ 
                                            backgroundColor: 'primary.light', 
                                            color: 'primary.contrastText',
                                            borderRadius: 1,
                                            px: 0.5,
                                            mr: 0.5,
                                            textTransform: 'capitalize'
                                          }}
                                        >
                                          {type}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </CardContent>
                                </Card>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No meals planned
                              </Typography>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>

        {/* Add/Edit Meal Plan Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingPlanId ? 'Edit Meal Plan' : 'Add Meal to Plan'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={planDate ? formatDateToYYYYMMDD(planDate) : ''}
                  onChange={(e) => setPlanDate(new Date(e.target.value))}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Meal Type</InputLabel>
                  <Select
                    value={mealType}
                    onChange={(e) => setMealType(e.target.value)}
                    label="Meal Type"
                  >
                    {mealTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        <Box display="flex" alignItems="center">
                          {getMealTypeIcon(type)}
                          <Typography sx={{ ml: 1, textTransform: 'capitalize' }}>
                            {type}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  label="Notes"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  margin="normal"
                  placeholder="Add any cooking notes or modifications"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Select a Meal
                </Typography>
                
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Search meals"
                  placeholder="Search by name or description"
                  value={mealSearchQuery}
                  onChange={(e) => setMealSearchQuery(e.target.value)}
                  margin="normal"
                />
                
                <Box sx={{ mt: 2, maxHeight: '300px', overflow: 'auto' }}>
                  {filteredMeals.length > 0 ? (
                    filteredMeals.map(meal => (
                      <Card 
                        key={meal._id}
                        onClick={() => setSelectedMeal(meal)}
                        sx={{ 
                          mb: 2, 
                          cursor: 'pointer',
                          border: selectedMeal?._id === meal._id ? 2 : 0,
                          borderColor: 'primary.main',
                          '&:hover': { boxShadow: 3 }
                        }}
                      >
                        <Box display="flex">
                          <CardMedia
                            component="img"
                            sx={{ width: 80, height: 80 }}
                            image={meal.imageUrl || `https://source.unsplash.com/random/80x80/?food`}
                            alt={meal.name}
                          />
                          <CardContent sx={{ flex: '1 0 auto', py: 1 }}>
                            <Typography variant="subtitle1">
                              {meal.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {meal.description || 'No description'}
                            </Typography>
                            <Box display="flex" mt={0.5}>
                              {meal.dietaryType?.map((type, index) => (
                                <Typography 
                                  key={index} 
                                  variant="caption" 
                                  sx={{ 
                                    backgroundColor: 'primary.light', 
                                    color: 'primary.contrastText',
                                    borderRadius: 1,
                                    px: 0.5,
                                    mr: 0.5,
                                    textTransform: 'capitalize'
                                  }}
                                >
                                  {type}
                                </Typography>
                              ))}
                            </Box>
                          </CardContent>
                        </Box>
                      </Card>
                    ))
                  ) : (
                    <Alert severity="info">
                      No meals found. Try a different search term.
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              disabled={!selectedMeal || !mealType}
            >
              {editingPlanId ? 'Update' : 'Add to Plan'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notifications */}
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default MealPlanner;
